import React, { FC, useCallback, useEffect } from "react";
import moment from "moment";
import _ from "lodash";

import Page from "../common/navigation/Page";
import Button from "../common/F7/Button";
import Spinner from "../common/F7/Spinner";
import FormFields from "../common/form/FieldsList";
import ErrorMessages from "../common/F7/ErrorMessages";
import FormButtons from "../common/form/Buttons";
import ExportButton from "../common/form/ExportButton";
import BackButton from "../common/navigation/BackButton";
import ContentTitle from "../common/F7/ContentTitle";
import F7 from "../../utils/F7";
import config from "../../config";
import { useNavigate, useParams } from "react-router-dom";
import usePermissions from "../../hooks/usePermissions";
import { Field } from "../common/form/FieldHandler";
import { useMap } from "../../App";
import { useJWT } from "../../state";
import { InventoryObject, useCurrentObject } from "../../hooks/database/useCurrentObject";
import { useCurrentInventory } from "../../hooks/database/useCurrentInventory";
import { useUpdateObject } from "../../hooks/database/useUpdateObject";
import { stringifyObject } from "./ObjectPage";
import { InventoryFieldValue } from "../../hooks/database/types/inventories";
import { ProjectField } from "../../hooks/database/types/projects";
import { compare } from "../../utils/utils";
import { useCurrentProjectConfig } from "../../hooks/database/useCurrentProjectConfig";
import { useForm } from "../../hooks/useForm";
import DangerZoneCard from "../common/F7/DangerZoneCard";

const shortTimeFormat = false;

function getSingleCoordinate(object: InventoryObject): ol.Coordinate {
  if (object.geom.geometry.type === "Point") {
    return object.geom.geometry.coordinates;
  } else if (object.geom.geometry.type === "LineString") {
    return object.geom.geometry.coordinates[0];
  } else if (object.geom.geometry.type === "Polygon") {
    return object.geom.geometry.coordinates[0][0];
  } else {
    throw new Error("Geometry type not implemented");
  }
}

const EditForm: FC<{
  object: InventoryObject;
  fields: Record<string, ProjectField | Field>;
  onSave: (fields: Record<string, Field>) => void;
  type: "new" | "edit";
  error: string[];
  id?: string;
}> = ({ object, fields, onSave, type, error, id }) => {
  const { canDeleteForm, canSaveGeom } = usePermissions();

  const { execute: updateObject } = useUpdateObject();
  const { data: projectConfig } = useCurrentProjectConfig();

  /**
   * Compares fields against each other using rule
   */
  const validate = useCallback(
    (fields: Record<string, Field>) => {
      // handle required fields first
      const missingRequired = Object.entries(fields)
        .filter(([_, field]) => field.required && (field.value === undefined || field.value === null))
        .map(([key, _]) => key);
      if (missingRequired.length > 0) {
        return Object.fromEntries(missingRequired.map((key) => [key, { message: "Field is required" }]));
      }

      const errors: Record<string, { message: string }> = {};

      //region rule validation
      const rule = projectConfig?.config?.blurValidation;
      const entries = Object.entries(fields).filter(([k, _]) => new RegExp(rule.field2Regex).test(k));

      entries.forEach(([key1, field1], j) => {
        // reason for `as unknown as string`: given type=number the value should already be one, but the previous code
        // always converted with parseFloat, so we'll leave it as this just in case
        // noinspection SuspiciousTypeOfGuard
        const v1 =
          field1.type === "number" && typeof field1.value !== "number" ? parseFloat(field1.value as unknown as string) : field1.value;
        entries.slice(j + 1).forEach(([_, field2]) => {
          if (field2.value === undefined || field2.value === null || field2.value === "") {
            return;
          }
          // see comment above
          // noinspection SuspiciousTypeOfGuard
          const v2 =
            field2.type === "number" && typeof field2.value !== "number" ? parseFloat(field2.value as unknown as string) : field2.value;
          if (!compare(v1, rule.operator, v2)) {
            errors[key1] = {
              message: rule.errorMessage,
            };
          }
        });
      });
      //endregion

      if ("tid" in fields && typeof fields["tid"].value === "string") {
        const value = fields["tid"].value;
        const regex = shortTimeFormat
          ? new RegExp(/^([01]?0\d|1\d|2[0-3]):[0-5]\d:([0-5]\d)$/)
          : new RegExp(/^([01]?0\d|1\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/);
        if (value.length > 0 && !regex.test(value)) {
          errors["tid"] = { message: "Enter as 'HH:MM:SS'" };
        }
      }

      /* Special handling for the magic "form name". It's just the value of the "first" field, though "first" is
       * ambiguous since fields are stored in a dictionary. Let's just hope it doesn't implode too soon.
       */
      const formNameKey = Object.keys(fields)[0];
      const formName = fields[formNameKey].value;
      if (!formName || typeof formName !== "string" || formName.length < 0) {
        errors[formNameKey] = { message: "Form name is required" };
      } else if (formName.charCodeAt(0) === 32) {
        errors[formNameKey] = { message: "Form name can not start with a blankspace" };
      }

      return errors;
    },
    [projectConfig?.config?.blurValidation]
  );

  const {
    fields: formFields,
    values: formValues,
    dirty: formDirty,
    setFieldOverride: setFormFieldOverride,
    setValue: setFormValue,
    handleChange,
    handleBlur,
  } = useForm(fields, validate);

  const map = useMap()!;
  /**
   * Special field handling: _3006, _4326, _currproj (each with optional ending lv, l or v) and _geotitle
   *
   * Fields those keys start with either _3006, _4326 or _currproj will be assigned a value equal to their coordinate,
   * transformed either to EPSG:4326 (WGS84), EPSG:3006 (SWEREF99 TM) or the current projection of the view.
   *
   * Fields those key equals _geotitle will be assigned the title of the object.
   */
  useEffect(() => {
    for (const key of Object.keys(formFields)) {
      if (key.startsWith("_3006") || key.startsWith("_4326") || key.startsWith("_currproj")) {
        const targetProjection = key.startsWith("_currproj") ? map.getView().getProjection() : ol.proj.get(`EPSG:${key.slice(1, 5)}`);
        const xy = ol.proj.transform(getSingleCoordinate(object), "EPSG:4326", targetProjection);
        if (key.endsWith("lv")) {
          setFormFieldOverride(key, { label: fields[key].label + " " + targetProjection.getCode() });
          setFormValue(key, `${targetProjection.getCode()} ${xy[0]}, ${xy[1]}`);
        } else if (key.endsWith("l")) {
          setFormFieldOverride(key, { label: fields[key].label + " " + targetProjection.getCode() });
          setFormValue(key, `${xy[0]}, ${xy[1]}`);
        } else if (key.endsWith("v")) {
          setFormValue(key, `${targetProjection.getCode()} ${xy[0]}, ${xy[1]}`);
        } else {
          setFormValue(key, `${xy[0]}, ${xy[1]}`);
        }
      } else if (key === "_geotitle") {
        setFormValue(key, object.title);
      }
    }
  }, [fields, formFields, map, object, setFormFieldOverride, setFormValue]);

  /**
   * Special field handling: _checksum and _checkbox
   *
   * Fields those key start with `_checksum` will automatically be assigned a value of `OK` or `NC`, depending on if all
   * fields those key start with `_checkbox` are checked.
   */
  useEffect(() => {
    const checksumKey = Object.keys(formValues).find((k) => k.startsWith("_checksum"));
    if (checksumKey) {
      const hasUnchecked = Object.entries(formValues)
        .filter(([key, _]) => key.startsWith("_checkbox"))
        .some(([_, value]) => value !== true);
      setFormValue(checksumKey, hasUnchecked ? "NC" : "OK", true);
    }
  }, [formValues, setFormValue]);

  const navigate = useNavigate();
  const handleCancel = useCallback(() => {
    if (!formDirty) {
      return navigate("../");
    }

    F7.confirm("Click cancel to stay in form", "Discard changes?", () => {
      navigate("../");
    });
  }, [formDirty, navigate]);

  const handleDelete = useCallback(() => {
    if (!object) {
      return;
    }

    F7.confirm("This action cannot be undone", "Confirm delete?", async () => {
      await updateObject({
        ...object,
        data: object.data.filter((el) => el._id !== id),
      });
      navigate("../");
    });
  }, [id, navigate, object, updateObject]);

  const handleSave = useCallback(() => {
    if (formDirty) {
      onSave(formFields);
    }

    navigate("../");
  }, [formDirty, formFields, navigate, onSave]);

  const hasBlurRule =
    projectConfig?.config?.blurValidation?.hasOwnProperty("field2Regex") &&
    projectConfig?.config?.blurValidation?.hasOwnProperty("errorMessage") &&
    projectConfig?.config?.blurValidation?.hasOwnProperty("operator");

  return (
    <Page noToolbar navbarLeft={<BackButton onClick={handleCancel}>Cancel</BackButton>} title={object.title}>
      <ContentTitle>
        Edit Form{" "}
        <big>
          <strong>{Object.values(formValues)[0] as string}</strong>
        </big>
      </ContentTitle>
      <FormFields
        fields={formFields}
        onChange={handleChange}
        onBlur={hasBlurRule && (type === "new" || projectConfig?.config?.blurValidation.allowOnEditForm) ? handleBlur : () => {}}
      />
      {error ? <ErrorMessages messages={error} /> : null}
      <hr />
      {canSaveGeom ? <FormButtons onSave={handleSave} /> : null}
      <ExportButton title={object.title} fields={formFields} />
      <div style={{ paddingTop: 20 }}>
        {canDeleteForm && type === "edit" ? (
          <DangerZoneCard header="Danger zone">
            <Button color="red" onClick={handleDelete}>
              Delete Form
            </Button>
          </DangerZoneCard>
        ) : null}
      </div>
    </Page>
  );
};

const ObjectEditFormPage: FC = () => {
  const params = useParams<{ formId: string; templateId?: undefined } | { formId?: undefined; templateId: string }>();

  const { data: object } = useCurrentObject();
  const { data: inventory } = useCurrentInventory();

  const existingForm = object?.data.find((d) => d._id === params.formId);

  const decodedJwt = useJWT();
  const { execute: updateObject, error } = useUpdateObject();
  const navigate = useNavigate();

  const saveNewForm = useCallback(
    async (fields: Record<string, Field>) => {
      if (!object || !params.templateId) {
        return;
      }

      const form = params.templateId;
      const docId = object._id + "_" + moment().format("YYYY-MM-DDTHH:mm:ss");
      const docInfoEncoded = btoa(
        stringifyObject({
          origin: "PIANO",
          created: moment().format(),
          cuser: decodedJwt!.userId,
          cprod: config.productionBuild,
          uorigin: "",
          updated: "",
          uuser: "",
          uprod: "",
        })
      );
      const addInfoEncoded = btoa(
        stringifyObject({
          isApp: config.isApp,
          appVersion: config.version,
          userAgent: window.navigator.userAgent,
          onLine: window.navigator.onLine,
        })
      );

      const fieldsWithValues: Record<string, InventoryFieldValue> = fields as Record<string, InventoryFieldValue>;
      // store stuff in storage that will be used as default values for the next new form
      Object.entries(fields).forEach(([key, field]) => {
        if (key.indexOf("_cc_") === 0) {
          const sesName = key.split("_cc_")[1];
          if (field.value && field.value !== "" && (field.value as string).indexOf(" ") !== 0) {
            sessionStorage.setItem(sesName, field.value as string);
          } else if ((!sessionStorage.getItem(sesName) && !field.value) || field.value === "") {
            sessionStorage.removeItem(sesName);
          }
        }
        if (key.indexOf("_signdt") === 0 && key.match("_signdt")) {
          if (
            field.value &&
            (field.value as string).indexOf("nosign") !== 0 &&
            field.value !== "" &&
            (field.value as string).indexOf(" ") !== 0
          ) {
            const sesSign = (field.value as string).split("_")[0] || undefined;
            if (
              !sessionStorage.getItem("sign") ||
              (sessionStorage.getItem("sign") && sesSign && sessionStorage.getItem("sign") !== sesSign)
            ) {
              sessionStorage.setItem("sign", sesSign!);
            } else if (!sesSign) {
              sessionStorage.removeItem("sign");
            }
          } else if (!field.value || field.value === "" || (field.value as string).indexOf(" ") !== 0) {
            sessionStorage.removeItem("sign");
          }
        }
      });
      await updateObject({
        ...object,
        data: [
          ...object.data,
          {
            _id: docId,
            docInfo: docInfoEncoded,
            addInfo: addInfoEncoded,
            created: moment().format(),
            updated: moment().format(),
            geomId: object._id,
            version: 2,
            legacyId: moment().format("YYYY-MM-DDTHH:mm:ss"),
            type: "formdata",
            form: form,
            values: fieldsWithValues,
          },
        ],
      });
      navigate(`../f/${docId}`);
    },
    [object, params.templateId, decodedJwt, updateObject, navigate]
  );

  const saveExistingForm = useCallback(
    async (fields: Record<string, Field>) => {
      if (!object) {
        return;
      }

      const data = _.cloneDeep(object.data || []);
      const formIndex = data.findIndex((i) => i._id === params.formId);
      const updated = moment().format();
      const docInfo = JSON.parse(atob(data[formIndex].docInfo).replace(/;/g, ","));
      data.splice(formIndex, 1, {
        ...data[formIndex],
        updated,
        values: _.mapValues(fields, (f: Field) => f as InventoryFieldValue),
        addInfo: btoa(
          stringifyObject({
            isApp: config.isApp,
            appVersion: config.version,
            userAgent: window.navigator.userAgent,
            onLine: window.navigator.onLine,
          })
        ),
        docInfo: btoa(
          stringifyObject({
            ...docInfo,
            uorigin: "PIANO",
            updated: updated,
            uuser: decodedJwt!.userId,
            uprod: config.productionBuild,
          })
        ),
      });

      await updateObject({
        ...object,
        data,
      });
    },
    [object, updateObject, params.formId, decodedJwt]
  );

  if (!object || !inventory) {
    return <Spinner />;
  } else if (params.templateId) {
    // create new from template

    /**
     * Special field handling: A bunch of fields that get assigned initial values based on their keys
     *
     * `_sign` - The signature of the user, as previously entered in a field also starting with that key
     * `_signdt` - Like `_sign`, but also appends the date and time
     * `_time` - The current time in the format HH:mm:ss
     * `_date` - The current date in the format YYYY-MM-DD
     * `_cc_` - The same value as previously entered in a field with the same key
     */
    const sign = sessionStorage.getItem("sign");
    const fields = _.mapValues(inventory.forms[params.templateId].fields, (field: ProjectField, key) => {
      if (key.includes("_sign")) {
        if (sign) {
          return { ...field, value: sign };
        }
      } else if (key.includes("_signdt")) {
        if (sign) {
          return { ...field, value: `${sign}_${moment().format()}` };
        } else {
          return { ...field, value: `nosign_${moment().format()}` };
        }
      } else if (key === "_time") {
        return { ...field, value: moment().format("HH:mm:ss") };
      } else if (key === "_date") {
        return { ...field, value: moment().format("YYYY-MM-DD") };
      } else if (key.indexOf("_cc_") === 0) {
        const nameInStorage = key.split("_cc_")[1];
        if (nameInStorage) {
          if (sessionStorage.getItem(nameInStorage)) {
            return { ...field, value: sessionStorage.getItem(nameInStorage) };
          }
        }
      }
      return field;
    });

    return <EditForm object={object} fields={fields} onSave={saveNewForm} type="new" error={error as string[]} />;
  } else if (existingForm) {
    return (
      <EditForm
        object={object}
        fields={existingForm.values}
        onSave={saveExistingForm}
        type="edit"
        error={error as string[]}
        id={existingForm._id}
      />
    );
  } else {
    return <div>Form not found</div>;
  }
};

export default ObjectEditFormPage;
